// AGENT-A5: UI-only Zustand store (not user-data filters).
//
// Lives separately from filterStore so re-renders driven by ephemeral UI state
// (sidebar collapse, search-in-sidebar) don't churn the data layer.

import { create } from "zustand";

export type ThemeMode = "light" | "dark";

const THEME_STORAGE_KEY = "hsq-dash-theme";

/** Initial theme: saved choice → OS preference → light. */
function initialTheme(): ThemeMode {
  if (typeof window === "undefined") return "light";
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === "dark" || saved === "light") return saved;
  } catch {
    /* localStorage blocked — fall through to OS preference. */
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function persistTheme(theme: ThemeMode): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

/** Apply the theme to `<html data-theme>` SYNCHRONOUSLY (not via a React
 *  effect). The ECharts charts resolve their axis/label colors with
 *  getComputedStyle inside a render-phase useMemo keyed on `theme`. If the
 *  attribute were only set in a post-render effect, the first render after a
 *  flip would resolve colors against the OLD theme and the chart would lag one
 *  flip behind (e.g. light-gray dark-mode labels persisting on the light-mode
 *  white background). Setting it here — before the re-render that the store
 *  update triggers — closes that gap. */
function applyThemeToDom(theme: ThemeMode): void {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", theme);
  }
}

// AGENT-DASH-RESIZE: user-draggable sidebar width (desktop only), persisted
// so the chosen width survives reloads.
const SIDEBAR_WIDTH_KEY = "hsq-dash-sidebar-w";
export const SIDEBAR_WIDTH_MIN = 200;
export const SIDEBAR_WIDTH_MAX = 560;
export const SIDEBAR_WIDTH_DEFAULT = 240;

function clampSidebarWidth(px: number): number {
  if (!Number.isFinite(px)) return SIDEBAR_WIDTH_DEFAULT;
  return Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, Math.round(px)));
}

function initialSidebarWidth(): number {
  if (typeof window === "undefined") return SIDEBAR_WIDTH_DEFAULT;
  try {
    const saved = Number(window.localStorage.getItem(SIDEBAR_WIDTH_KEY));
    if (Number.isFinite(saved) && saved > 0) return clampSidebarWidth(saved);
  } catch {
    /* localStorage blocked */
  }
  return SIDEBAR_WIDTH_DEFAULT;
}

function persistSidebarWidth(px: number): void {
  try {
    window.localStorage.setItem(SIDEBAR_WIDTH_KEY, String(px));
  } catch {
    /* ignore */
  }
}

export interface UiState {
  /** Whether the responsive sidebar drawer is open (only relevant <1100px). */
  sidebarOpen: boolean;
  /** Desktop (≥1100px) only: collapse the 240px sidebar column so the
   *  visuals get the full width. Independent of the mobile `sidebarOpen`
   *  drawer. */
  sidebarCollapsed: boolean;
  /** Desktop (≥1100px) only: user-chosen sidebar column width in px,
   *  draggable via the resize handle. Clamped + persisted. */
  sidebarWidth: number;
  /** Light / dark theme. Applied to document.documentElement[data-theme]. */
  theme: ThemeMode;
  /** Live search-in-sidebar query for the categories list. */
  categorySearch: string;
  /** Which category groups are expanded (to show parent-product children). */
  expandedCategories: Set<string>;
  /** AGENT-A10: whether the "View Raw Tables" modal is open. The TopBar
   *  toggles this via setRawTablesOpen / openRawTables; the modal listens
   *  to this flag to mount its overlay. */
  rawTablesOpen: boolean;

  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setSidebarWidth: (px: number) => void;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  setCategorySearch: (q: string) => void;
  toggleCategoryExpanded: (name: string) => void;
  /** AGENT-A10: open/close the raw-tables modal. */
  setRawTablesOpen: (open: boolean) => void;
  openRawTables: () => void;
  closeRawTables: () => void;
}

function toggleInSet<T>(s: Set<T>, item: T): Set<T> {
  const next = new Set(s);
  if (next.has(item)) next.delete(item);
  else next.add(item);
  return next;
}

// Apply the boot theme to <html> at module load so the very first render — and
// the charts' getComputedStyle reads — already see the correct CSS vars (no
// one-flip lag / flash on initial load). Idempotent with the in-action calls.
applyThemeToDom(initialTheme());

export const useUiStore = create<UiState>((set) => ({
  sidebarOpen: false,
  sidebarCollapsed: false,
  sidebarWidth: initialSidebarWidth(),
  theme: initialTheme(),
  categorySearch: "",
  expandedCategories: new Set<string>(),
  // AGENT-A10: raw-tables modal starts closed.
  rawTablesOpen: false,

  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  toggleSidebarCollapsed: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  setSidebarWidth: (px) => {
    const clamped = clampSidebarWidth(px);
    persistSidebarWidth(clamped);
    set({ sidebarWidth: clamped });
  },
  setTheme: (theme) => {
    persistTheme(theme);
    applyThemeToDom(theme);
    set({ theme });
  },
  toggleTheme: () =>
    set((s) => {
      const next: ThemeMode = s.theme === "dark" ? "light" : "dark";
      persistTheme(next);
      applyThemeToDom(next);
      return { theme: next };
    }),
  setCategorySearch: (q) => set({ categorySearch: q }),
  toggleCategoryExpanded: (name) =>
    set((s) => ({ expandedCategories: toggleInSet(s.expandedCategories, name) })),
  // AGENT-A10: raw-tables modal actions.
  setRawTablesOpen: (open) => set({ rawTablesOpen: open }),
  openRawTables: () => set({ rawTablesOpen: true }),
  closeRawTables: () => set({ rawTablesOpen: false }),
}));
